import type { FieldErrors, UseFormRegister } from 'react-hook-form';

import { Checkbox, SelectField, TextField } from '@shared';
import { useTranslations } from '@/i18n/client';
import type { CountryOption } from '@/services/api/types';

import { useCountryOptions } from './useCountryOptions';

export interface AddressFormData {
  companyName: string;
  street: string;
  zip: string;
  city: string;
  countryID: string;
  phone: string;
  email: string;
  vat: string;
  isBillingAddress: boolean;
  isShippingAddress: boolean;
}

export interface AddressFieldsProps {
  register: UseFormRegister<AddressFormData>;
  errors: FieldErrors<AddressFormData>;
  translationNamespace: 'registration' | 'settings';
  hideCompanyName?: boolean;
  countryOptions?: CountryOption[];
}

function isAddressRequired(formValues: AddressFormData): boolean {
  return !('addAddress' in formValues) || Boolean((formValues as { addAddress?: boolean }).addAddress);
}

export function AddressFields({
  register,
  errors,
  translationNamespace,
  hideCompanyName,
  countryOptions,
}: AddressFieldsProps) {
  const t = useTranslations(translationNamespace);
  const { options: loadedCountryOptions } = useCountryOptions();
  const resolvedCountryOptions = countryOptions ?? loadedCountryOptions;

  return (
    <>
      {hideCompanyName ? null : (
        <TextField
          label={t('addresses.companyNameLabel')}
          type="text"
          {...register('companyName', {
            validate: (value, formValues) =>
              !isAddressRequired(formValues) ||
              Boolean(value.trim()) ||
              t('addresses.errors.companyNameRequired'),
          })}
          message={errors.companyName?.message}
          invalid={Boolean(errors.companyName)}
          required
        />
      )}

      <TextField
        label={t('addresses.streetLabel')}
        type="text"
        {...register('street', {
          validate: (value, formValues) =>
            !isAddressRequired(formValues) || Boolean(value.trim()) || t('addresses.errors.streetRequired'),
        })}
        message={errors.street?.message}
        invalid={Boolean(errors.street)}
        required
      />

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="sm:flex-1">
          <TextField
            label={t('addresses.zipLabel')}
            type="text"
            {...register('zip', {
              validate: (value, formValues) =>
                !isAddressRequired(formValues) || Boolean(value.trim()) || t('addresses.errors.zipRequired'),
            })}
            message={errors.zip?.message}
            invalid={Boolean(errors.zip)}
            required
          />
        </div>

        <div className="sm:flex-1">
          <TextField
            label={t('addresses.cityLabel')}
            type="text"
            {...register('city', {
              validate: (value, formValues) =>
                !isAddressRequired(formValues) || Boolean(value.trim()) || t('addresses.errors.cityRequired'),
            })}
            message={errors.city?.message}
            invalid={Boolean(errors.city)}
            required
          />
        </div>
      </div>

      <SelectField
        label={t('addresses.countryLabel')}
        {...register('countryID', {
          validate: (value, formValues) =>
            !isAddressRequired(formValues) || Boolean(value.trim()) || t('addresses.errors.countryRequired'),
        })}
        message={errors.countryID?.message}
        invalid={Boolean(errors.countryID)}
        required
      >
        <option value="">{t('addresses.countryPlaceholder')}</option>
        {resolvedCountryOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </SelectField>

      <TextField label={t('addresses.phoneLabel')} type="text" {...register('phone')} />

      <TextField label={t('addresses.emailLabel')} type="email" {...register('email')} />

      <TextField label={t('addresses.vatLabel')} type="text" {...register('vat')} />

      <Checkbox label={t('addresses.billingAddressLabel')} {...register('isBillingAddress')} />

      <Checkbox label={t('addresses.shippingAddressLabel')} {...register('isShippingAddress')} />
    </>
  );
}
